import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Package, ShoppingCart, PaintBucket, MessageSquare, DollarSign, Plus, Trash2, Edit3, X, Briefcase, ExternalLink, ChevronRight, ChevronUp, ChevronDown, Upload, Image as ImageIcon, Download, Clock, CheckCircle2, Gift, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const AdminDashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [bulkQuotes, setBulkQuotes] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [giftForm, setGiftForm] = useState({ amount: "", buyer_email: "", buyer_name: "", send_email: false });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [siteConfig, setSiteConfig] = useState({ hero_image: "", hero_link: "", hero_alt: "" });
  const [heroUploading, setHeroUploading] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [emailChannels, setEmailChannels] = useState(null);
  const heroFileRef = React.useRef(null);

  const handleHeroUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setHeroUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.put("/admin/site-config", { hero_image: up.data.url });
      setSiteConfig(r.data);
      toast.success("Hero image updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setHeroUploading(false);
      if (heroFileRef.current) heroFileRef.current.value = "";
    }
  };

  const saveHeroFields = async () => {
    try {
      const r = await api.put("/admin/site-config", {
        hero_link: siteConfig.hero_link,
        hero_alt: siteConfig.hero_alt,
      });
      setSiteConfig(r.data);
      toast.success("Homepage settings saved");
    } catch (err) {
      toast.error("Save failed");
    }
  };

  const saveConfig = async (patch, msg = "Saved") => {
    try {
      const r = await api.put("/admin/site-config", patch);
      setSiteConfig(r.data);
      toast.success(msg);
    } catch (err) {
      toast.error("Save failed");
    }
  };

  const exportPirateShip = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/orders/export/pirateship", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "pirateship_orders.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PirateShip CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) nav("/admin/login");
  }, [user, authLoading, nav]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/custom-orders").then((r) => setCustomOrders(r.data)).catch(() => {});
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/admin/bulk-quotes").then((r) => setBulkQuotes(r.data)).catch(() => {});
    api.get("/admin/giftcards").then((r) => setGiftCards(r.data)).catch(() => {});
    api.get("/site-config").then((r) => setSiteConfig(r.data)).catch(() => {});
    api.get("/admin/herturn/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
    api.get("/admin/email-channels").then((r) => setEmailChannels(r.data)).catch(() => {});
  };

  const toggleEmailChannel = async (key, value) => {
    try {
      const r = await api.put("/admin/email-channels", { resend: { [key]: value } });
      setEmailChannels((prev) => ({ ...prev, resend: r.data.resend }));
      toast.success(value ? "Switched back to Resend" : "Now Klaviyo-only");
    } catch {
      toast.error("Update failed");
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (!user) return null;

  const handleDelete = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const handleToggleVisibility = async (p) => {
    const next = p.status === "draft" ? "live" : "draft";
    try {
      await api.put(`/admin/products/${p.id}/quick`, { status: next });
      toast.success(next === "live" ? "Product is now Live" : "Moved to Draft (hidden)");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const handleFulfillPreorder = async (id) => {
    if (!confirm("Mark this pre-order as fulfilled and notify the customer?")) return;
    try {
      await api.post(`/admin/orders/${id}/fulfill-preorder`);
      toast.success("Customer notified · pre-order fulfilled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fulfill");
    }
  };

  const handleIssueGift = async () => {
    const amt = parseFloat(giftForm.amount);
    if (!amt || amt < 1) return toast.error("Enter a valid amount");
    try {
      await api.post("/admin/giftcards", {
        amount: amt,
        buyer_email: giftForm.buyer_email || null,
        buyer_name: giftForm.buyer_name || null,
        send_email: giftForm.send_email && !!giftForm.buyer_email,
      });
      toast.success("Gift card issued");
      setGiftForm({ amount: "", buyer_email: "", buyer_name: "", send_email: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue");
    }
  };

  const handleDeactivateGift = async (card) => {
    const reactivate = card.status === "deactivated";
    try {
      await api.put(`/admin/giftcards/${card.id}/${reactivate ? "reactivate" : "deactivate"}`);
      toast.success(reactivate ? "Gift card reactivated" : "Gift card deactivated");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const handleUpdateCustom = async (id, status) => {    await api.put(`/admin/custom-orders/${id}`, { status });
    toast.success("Updated");
    load();
  };

  const handleReorderProduct = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= products.length) return;
    const a = products[idx];
    const b = products[j];
    const aSort = a.sort_order ?? 100;
    const bSort = b.sort_order ?? 100;
    // Optimistic UI: swap in place
    const next = [...products];
    next[idx] = { ...b, sort_order: aSort };
    next[j] = { ...a, sort_order: bSort };
    setProducts(next);
    try {
      await Promise.all([
        api.put(`/admin/products/${a.id}/sort`, { sort_order: bSort }),
        api.put(`/admin/products/${b.id}/sort`, { sort_order: aSort }),
      ]);
      toast.success("Order updated");
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: DollarSign },
    { key: "products", label: "Products", icon: Package },
    { key: "orders", label: "Orders", icon: ShoppingCart },
    { key: "preorders", label: "Pre-Orders", icon: Clock },
    { key: "custom", label: "Custom", icon: PaintBucket },
    { key: "bulk", label: "Bulk Quotes", icon: Briefcase },
    { key: "messages", label: "Messages", icon: MessageSquare },
    { key: "giftcards", label: "Gift Cards", icon: Gift },
    { key: "homepage", label: "Homepage", icon: ImageIcon },
    { key: "emails", label: "Emails", icon: Mail },
  ];

  return (
    <main data-testid="admin-dashboard" className="max-w-7xl mx-auto px-5 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-black text-4xl text-[#000000]">Studio Control Room</h1>
          <p className="text-sm text-[#333333]">Signed in as {user.email}</p>
        </div>
        <button
          data-testid="admin-logout"
          onClick={() => { logout(); nav("/"); }}
          className="inline-flex items-center gap-2 bg-white border-2 border-[#000000] px-4 py-2 rounded-full brutal font-semibold"
        >
          <LogOut size={16} /> Sign out
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full border-2 border-[#000000] font-bold text-sm ${
              tab === t.key ? "bg-[#000000] text-[#FFFFFF]" : "bg-white text-[#000000]"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: "Revenue (paid)", value: `$${stats.revenue}`, color: "#CCDE00", testid: "stat-revenue" },
            { label: "Products", value: stats.products, color: "#F3E30A", testid: "stat-products" },
            { label: "Orders", value: stats.orders, color: "#EC008C", testid: "stat-orders" },
            { label: "Paid Orders", value: stats.paid_orders, color: "#EC008C", testid: "stat-paid" },
            { label: "Custom Requests", value: stats.custom_orders, color: "#000000", testid: "stat-custom" },
            { label: "Messages", value: stats.messages, color: "#F7F7F5", testid: "stat-messages" },
          ].map((s) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className="brutal-static rounded-3xl p-5"
              style={{ background: s.color, color: ["#F3E30A", "#F7F7F5"].includes(s.color) ? "#000000" : "#FFFFFF" }}
            >
              <div className="text-xs font-bold uppercase tracking-widest opacity-70">{s.label}</div>
              <div className="font-display font-black text-3xl mt-2">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "products" && (
        <div>
          <button
            data-testid="new-product-btn"
            onClick={() => { setEditing({ slug: "", name: "", category: "backpacks", price: 0, description: "", images: [""], colors: [], sizes: [], featured: false, status: "draft" }); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-[#EC008C] text-[#FFFFFF] font-bold px-4 py-2 rounded-full brutal mb-5"
          >
            <Plus size={16} /> Add Product
          </button>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p, idx) => (
              <div key={p.id} data-testid={`admin-product-${p.slug}`} className="brutal-static bg-white rounded-2xl p-4">
                <div className="relative">
                  <img src={(p.images || [])[0]} alt={p.name} className="w-full h-32 object-cover rounded-xl border-2 border-[#000000]" />
                  <div className="absolute top-2 right-2 flex flex-col gap-1">
                    <button
                      data-testid={`reorder-up-${p.slug}`}
                      onClick={() => handleReorderProduct(idx, -1)}
                      disabled={idx === 0}
                      title="Move up in catalog"
                      className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center border-2 border-black hover:bg-[#EC008C] disabled:opacity-30"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      data-testid={`reorder-down-${p.slug}`}
                      onClick={() => handleReorderProduct(idx, 1)}
                      disabled={idx === products.length - 1}
                      title="Move down in catalog"
                      className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center border-2 border-black hover:bg-[#EC008C] disabled:opacity-30"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <div className="absolute top-2 left-2 bg-[#DCE842] text-black text-[10px] font-black px-2 py-1 rounded border-2 border-black">
                    #{idx + 1}
                  </div>
                  <div className="absolute bottom-2 left-2 flex gap-1">
                    <span className={`text-[10px] font-black px-2 py-1 rounded border-2 border-black ${p.status === "draft" ? "bg-[#9CA3AF] text-black" : "bg-[#2A9D8F] text-white"}`}>
                      {p.status === "draft" ? "DRAFT" : "LIVE"}
                    </span>
                    {p.preorder && (
                      <span className="text-[10px] font-black px-2 py-1 rounded border-2 border-black bg-[#EC008C] text-white">PRE-ORDER</span>
                    )}
                  </div>
                </div>
                <div className="font-display font-bold mt-2 text-[#000000]">{p.name}</div>
                <div className="text-sm text-[#333333]">${p.price} · {p.category}</div>
                <div className="mt-3 flex gap-2">
                  <button
                    data-testid={`toggle-visibility-${p.slug}`}
                    onClick={() => handleToggleVisibility(p)}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-full border-2 border-[#000000] text-xs font-bold ${p.status === "draft" ? "bg-[#2A9D8F] text-white" : "bg-white text-[#000000]"}`}
                  >
                    {p.status === "draft" ? "Go Live" : "Unpublish"}
                  </button>
                  <button
                    data-testid={`edit-${p.slug}`}
                    onClick={() => { setEditing(p); setShowForm(true); }}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-full border-2 border-[#000000] text-xs font-bold bg-[#F3E30A]"
                  >
                    <Edit3 size={12} /> Edit
                  </button>
                  <button
                    data-testid={`delete-${p.slug}`}
                    onClick={() => handleDelete(p.id)}
                    className="flex items-center justify-center gap-1 py-2 px-3 rounded-full border-2 border-[#000000] text-xs font-bold bg-white text-[#EC008C]"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              data-testid="export-pirateship-btn"
              onClick={exportPirateShip}
              disabled={exporting}
              className="inline-flex items-center gap-2 bg-[#000000] text-[#FFFFFF] font-bold text-sm px-4 py-2 rounded-full brutal disabled:opacity-60"
            >
              <Download size={16} /> {exporting ? "Exporting..." : "Export to PirateShip"}
            </button>
          </div>
          {orders.length === 0 ? <div className="text-[#333333]">No orders yet.</div> : orders.map((o) => (
            <Link
              key={o.id}
              to={`/admin/orders/${o.id}`}
              data-testid={`order-${o.id}`}
              className="brutal-static bg-white rounded-2xl p-4 flex items-center gap-4 flex-wrap hover:bg-[#F3E30A]/20 transition-colors cursor-pointer"
            >
              <div className="flex-1 min-w-[200px]">
                <div className="font-bold text-[#000000]">{o.customer_name} · {o.customer_email}</div>
                <div className="text-xs text-[#333333]">{new Date(o.created_at).toLocaleString()}</div>
                <div className="text-sm mt-1">{o.items.length} items · ${o.total.toFixed(2)}</div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 border-[#000000] ${o.payment_status === "paid" ? "bg-[#CCDE00] text-[#000000]" : "bg-[#F3E30A]"}`}>
                {o.payment_status}
              </span>
              <ChevronRight size={18} className="text-black/40" />
            </Link>
          ))}
        </div>
      )}

      {tab === "preorders" && (
        <div className="space-y-3" data-testid="preorders-tab">
          {orders.filter((o) => o.is_preorder).length === 0 ? (
            <div className="text-[#333333]">No pre-orders yet.</div>
          ) : orders.filter((o) => o.is_preorder).map((o) => {
            const expected = (o.items.find((i) => i.preorder_date) || {}).preorder_date;
            const note = (o.items.find((i) => i.preorder_note) || {}).preorder_note;
            return (
              <div key={o.id} data-testid={`preorder-${o.id}`} className="brutal-static bg-white rounded-2xl p-4 flex items-center gap-4 flex-wrap">
                <Link to={`/admin/orders/${o.id}`} className="flex-1 min-w-[220px]">
                  <div className="font-bold text-[#000000]">{o.customer_name} · {o.customer_email}</div>
                  <div className="text-xs text-[#333333]">{new Date(o.created_at).toLocaleString()}</div>
                  <div className="text-sm mt-1">{o.items.length} items · ${o.total.toFixed(2)}</div>
                  <div className="text-xs mt-1 text-[#EC008C] font-bold">
                    {o.requires_shipping ? "Ship" : "Local Pickup"}{expected ? ` · Expected: ${expected}` : ""}{note ? ` · ${note}` : ""}
                  </div>
                </Link>
                <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 border-[#000000] ${o.payment_status === "paid" ? "bg-[#CCDE00] text-[#000000]" : "bg-[#F3E30A]"}`}>
                  {o.payment_status}
                </span>
                {o.preorder_fulfilled ? (
                  <span data-testid={`preorder-fulfilled-${o.id}`} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border-2 border-[#000000] bg-[#2A9D8F] text-white">
                    <CheckCircle2 size={13} /> Fulfilled
                  </span>
                ) : (
                  <button
                    data-testid={`fulfill-preorder-${o.id}`}
                    onClick={() => handleFulfillPreorder(o.id)}
                    className="inline-flex items-center gap-1 bg-[#EC008C] text-white font-bold text-xs px-4 py-2 rounded-full brutal"
                  >
                    <CheckCircle2 size={14} /> Mark Fulfilled
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}


      {tab === "custom" && (
        <div className="space-y-3">
          {customOrders.length === 0 ? <div className="text-[#333333]">No custom design requests yet.</div> : customOrders.map((c) => (
            <Link
              key={c.id}
              to={`/admin/custom-orders/${c.id}`}
              data-testid={`custom-${c.id}`}
              className="brutal-static bg-white rounded-2xl p-4 flex items-center gap-4 flex-wrap hover:bg-[#F3E30A]/20 transition-colors cursor-pointer"
            >
              <div className="flex-1 min-w-[200px]">
                <div className="font-bold text-[#000000]">{c.customer_name} · {c.customer_email}</div>
                <div className="text-xs text-[#333333]">{new Date(c.created_at).toLocaleString()}</div>
                <div className="text-sm mt-1">Product: <strong>{c.product_type}</strong> · Qty: {c.quantity}</div>
              </div>
              <span className="px-3 py-1 rounded-full text-xs font-bold border-2 border-[#000000] bg-[#F3E30A]">
                {c.status}
              </span>
              <ChevronRight size={18} className="text-black/40" />
            </Link>
          ))}
        </div>
      )}

      {tab === "bulk" && (
        <div data-testid="bulk-quotes-panel" className="space-y-3">
          {bulkQuotes.length === 0 ? (
            <div className="text-[#333333]">No bulk quote requests yet.</div>
          ) : (
            bulkQuotes.map((q) => {
              const locs = (q.print_locations || []).join(", ") || "-";
              const badgeColor = {
                "screen-printing": "#DCE842",
                embroidery: "#EC008C",
                design: "#F3E30A",
                "strategy-call": "#111111",
                "purchase-order": "#111111",
              }[q.service] || "#F7F7F5";
              const badgeText = ["#DCE842", "#F3E30A", "#F7F7F5"].includes(badgeColor) ? "#000" : "#FFF";
              return (
                <Link
                  key={q.id}
                  to={`/admin/bulk-quotes/${q.id}`}
                  data-testid={`bulk-quote-${q.id}`}
                  className="block brutal-static bg-white rounded-2xl p-5 hover:bg-[#F3E30A]/20 transition-colors"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-black text-lg text-[#000]">
                        {q.company || q.name}
                      </div>
                      <div className="text-sm text-[#333]">
                        {q.name} · <span className="underline text-[#EC008C]">{q.email}</span>
                        {q.phone ? <span> · {q.phone}</span> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-3 py-1 rounded-full text-[10px] uppercase font-black tracking-widest"
                        style={{ background: badgeColor, color: badgeText }}
                      >
                        {q.service.replace("-", " ")}
                      </span>
                      <span className="text-xs text-[#555] font-mono">
                        {new Date(q.created_at).toLocaleString()}
                      </span>
                      <ChevronRight size={18} className="text-black/40" />
                    </div>
                  </div>

                  <div className="mt-4 grid md:grid-cols-4 gap-3 text-sm">
                    <MetaCell label="Garment" value={q.garment_type || "-"} />
                    <MetaCell label="Quantity" value={q.quantity || "-"} />
                    <MetaCell label="Deadline" value={q.deadline || "-"} />
                    <MetaCell label="Budget" value={q.budget || "-"} />
                  </div>

                  <div className="mt-3 text-sm">
                    <span className="text-[10px] uppercase tracking-widest font-black text-[#EC008C] mr-2">Locations:</span>
                    <span className="text-[#333]">{locs}</span>
                  </div>

                  {q.notes && (
                    <div className="mt-3 border-l-4 border-[#EC008C] pl-4 py-1">
                      <div className="text-[10px] uppercase tracking-widest font-black text-[#EC008C] mb-1">Notes</div>
                      <p className="text-sm text-[#333] whitespace-pre-wrap">{q.notes}</p>
                    </div>
                  )}

                  {q.artwork_url && (
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#EC008C]">
                      <ExternalLink size={12} /> Artwork attached
                    </div>
                  )}
                </Link>
              );
            })
          )}
        </div>
      )}

      {tab === "giftcards" && (
        <div className="space-y-4" data-testid="giftcards-tab">
          <div className="brutal-static bg-white rounded-2xl p-4">
            <div className="font-display font-black text-lg mb-3 flex items-center gap-2"><Gift size={18} /> Issue a gift card</div>
            <div className="grid sm:grid-cols-4 gap-2">
              <input data-testid="gift-issue-amount" type="number" placeholder="Amount $" value={giftForm.amount} onChange={(e) => setGiftForm({ ...giftForm, amount: e.target.value })} className="px-3 py-2 border-2 border-black rounded-xl" />
              <input data-testid="gift-issue-name" placeholder="Recipient name" value={giftForm.buyer_name} onChange={(e) => setGiftForm({ ...giftForm, buyer_name: e.target.value })} className="px-3 py-2 border-2 border-black rounded-xl" />
              <input data-testid="gift-issue-email" placeholder="Recipient email" value={giftForm.buyer_email} onChange={(e) => setGiftForm({ ...giftForm, buyer_email: e.target.value })} className="px-3 py-2 border-2 border-black rounded-xl" />
              <button data-testid="gift-issue-btn" onClick={handleIssueGift} className="bg-[#EC008C] text-white font-black rounded-xl px-4 py-2 brutal">Issue</button>
            </div>
            <label className="flex items-center gap-2 text-xs font-bold mt-2">
              <input type="checkbox" checked={giftForm.send_email} onChange={(e) => setGiftForm({ ...giftForm, send_email: e.target.checked })} /> Email the card to the recipient
            </label>
          </div>
          {giftCards.length === 0 ? <div className="text-[#333333]">No gift cards issued yet.</div> : giftCards.map((g) => (
            <div key={g.id} data-testid={`giftcard-${g.id}`} className="brutal-static bg-white rounded-2xl p-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="font-mono font-black text-black">{g.code}</div>
                <div className="text-xs text-[#333]">{g.buyer_name || "—"}{g.buyer_email ? ` · ${g.buyer_email}` : ""} · issued {g.issued_by}</div>
                <div className="text-sm mt-1">Balance <strong>${Number(g.balance).toFixed(2)}</strong> of ${Number(g.initial_amount).toFixed(2)}</div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-black border-2 border-black ${g.status === "active" ? "bg-[#2A9D8F] text-white" : g.status === "used" ? "bg-[#9CA3AF] text-black" : "bg-[#E5563A] text-white"}`}>
                {g.status}
              </span>
              <button data-testid={`giftcard-toggle-${g.id}`} onClick={() => handleDeactivateGift(g)} className="px-3 py-2 rounded-full border-2 border-black text-xs font-bold bg-white">
                {g.status === "deactivated" ? "Reactivate" : "Deactivate"}
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "messages" && (
        <div className="space-y-3">
          {messages.length === 0 ? <div className="text-[#333333]">No messages yet.</div> : messages.map((m) => (
            <Link
              key={m.id}
              to={`/admin/messages/${m.id}`}
              data-testid={`msg-${m.id}`}
              className="block brutal-static bg-white rounded-2xl p-4 hover:bg-[#F3E30A]/20 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="font-bold text-[#000000]">{m.name} · <span className="text-[#333333] font-normal">{m.email}</span></div>
                  <div className="text-xs text-[#333333]">{new Date(m.created_at).toLocaleString()}</div>
                  <p className="mt-2 text-sm line-clamp-2">{m.message}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {m.status === "replied" ? (
                    <span className="px-3 py-1 rounded-full text-[10px] uppercase font-black tracking-widest border-2 border-black bg-[#CCDE00]">Replied</span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-[10px] uppercase font-black tracking-widest border-2 border-black bg-[#F3E30A]">New</span>
                  )}
                  <ChevronRight size={18} className="text-black/40" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {tab === "emails" && (
        <div className="space-y-4" data-testid="emails-tab">
          <div className="brutal-static bg-white rounded-2xl p-5">
            <div className="font-display font-black text-lg mb-1 flex items-center gap-2"><Mail size={18} /> Email delivery</div>
            <p className="text-xs text-[#333] mb-4 leading-relaxed">
              Every email also fires a <strong>Klaviyo</strong> event. Keep a type on <strong>Resend</strong> until its
              Klaviyo Flow is built, approved &amp; live — then flip it to <strong>Klaviyo-only</strong>. Shipping
              Notification is already Klaviyo-only (no Resend version exists).
            </p>
            <div className="space-y-2" data-testid="email-channels-list">
              {emailChannels && Object.entries(emailChannels.metrics).map(([key, label]) => {
                const hasResend = key in emailChannels.resend;
                const on = emailChannels.resend[key];
                return (
                  <div key={key} className="flex items-center justify-between border-2 border-black rounded-xl px-4 py-3">
                    <div>
                      <div className="font-bold text-sm">{label}</div>
                      <div className="text-[11px] text-[#777]">Klaviyo metric: {label}</div>
                    </div>
                    {hasResend ? (
                      <button
                        data-testid={`email-toggle-${key}`}
                        onClick={() => toggleEmailChannel(key, !on)}
                        className={`text-xs font-black rounded-full px-4 py-2 border-2 border-black ${
                          on ? "bg-[#CCDE00] text-black" : "bg-black text-white"
                        }`}
                      >
                        {on ? "Resend + Klaviyo" : "Klaviyo-only"}
                      </button>
                    ) : (
                      <span className="text-xs font-black rounded-full px-4 py-2 border-2 border-black bg-black text-white">
                        Klaviyo-only
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === "homepage" && (
        <div className="space-y-4" data-testid="homepage-tab">
          <div className="brutal-static bg-white rounded-2xl p-5">
            <div className="font-display font-black text-lg mb-1 flex items-center gap-2"><ImageIcon size={18} /> Home hero banner</div>
            <p className="text-xs text-[#333] mb-4">This is the large banner image at the top of the homepage.</p>
            <div className="rounded-xl overflow-hidden border-2 border-black bg-[#FAFAF7] mb-4">
              {siteConfig.hero_image ? (
                <img data-testid="hero-preview" src={siteConfig.hero_image} alt="Current hero" className="w-full h-auto block" />
              ) : (
                <div className="py-16 text-center text-[#999]">No hero image set</div>
              )}
            </div>
            <input ref={heroFileRef} type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" />
            <button
              data-testid="hero-upload-btn"
              onClick={() => heroFileRef.current?.click()}
              disabled={heroUploading}
              className="inline-flex items-center gap-2 bg-[#000000] text-white font-black rounded-full px-5 py-2 brutal disabled:opacity-50"
            >
              <Upload size={16} /> {heroUploading ? "Uploading…" : "Change hero image"}
            </button>

            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[#333]">Clicks through to</label>
                <input
                  data-testid="hero-link-input"
                  value={siteConfig.hero_link || ""}
                  onChange={(e) => setSiteConfig({ ...siteConfig, hero_link: e.target.value })}
                  placeholder="/homecoming"
                  className="w-full mt-1 px-3 py-2 border-2 border-black rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[#333]">Image description (alt text)</label>
                <input
                  data-testid="hero-alt-input"
                  value={siteConfig.hero_alt || ""}
                  onChange={(e) => setSiteConfig({ ...siteConfig, hero_alt: e.target.value })}
                  placeholder="Describe the banner"
                  className="w-full mt-1 px-3 py-2 border-2 border-black rounded-xl"
                />
              </div>
            </div>
            <button
              data-testid="hero-save-btn"
              onClick={saveHeroFields}
              className="mt-4 inline-flex items-center gap-2 bg-[#EC008C] text-white font-black rounded-full px-5 py-2 brutal"
            >
              Save settings
            </button>
          </div>

          {/* Global local pickup */}
          <div className="brutal-static bg-white rounded-2xl p-5">
            <div className="font-display font-black text-lg mb-1 flex items-center gap-2"><Package size={18} /> Local pickup</div>
            <p className="text-xs text-[#333] mb-3">Turn this on to offer free local pickup on <strong>every</strong> product at checkout, without editing each listing.</p>
            <label className="flex items-center gap-3 font-bold text-sm">
              <input
                data-testid="pickup-global-toggle"
                type="checkbox"
                checked={!!siteConfig.local_pickup_global}
                onChange={(e) => saveConfig({ local_pickup_global: e.target.checked }, e.target.checked ? "Local pickup ON for all products" : "Global local pickup OFF")}
              />
              Offer local pickup on all products {siteConfig.local_pickup_global ? "(on)" : "(per-product only)"}
            </label>
          </div>

          {/* Homecoming toggle */}
          <div className="brutal-static bg-white rounded-2xl p-5">
            <div className="font-display font-black text-lg mb-1 flex items-center gap-2"><Clock size={18} /> Homecoming page</div>
            <p className="text-xs text-[#333] mb-4">Show or hide the Homecoming nav link &amp; promo. Turn it on ahead of a future event.</p>
            <label className="flex items-center gap-3 font-bold text-sm mb-3">
              <input
                data-testid="homecoming-toggle"
                type="checkbox"
                checked={!!siteConfig.homecoming_enabled}
                onChange={(e) => saveConfig({ homecoming_enabled: e.target.checked }, e.target.checked ? "Homecoming turned ON" : "Homecoming turned OFF")}
              />
              Homecoming enabled {siteConfig.homecoming_enabled ? "(live in nav)" : "(hidden)"}
            </label>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[#333]">Event date (optional)</label>
              <div className="flex gap-2 mt-1">
                <input
                  data-testid="homecoming-date-input"
                  value={siteConfig.homecoming_date || ""}
                  onChange={(e) => setSiteConfig({ ...siteConfig, homecoming_date: e.target.value })}
                  placeholder="e.g. October 17, 2026"
                  className="flex-1 px-3 py-2 border-2 border-black rounded-xl"
                />
                <button
                  data-testid="homecoming-date-save"
                  onClick={() => saveConfig({ homecoming_date: siteConfig.homecoming_date || "" }, "Date saved")}
                  className="bg-black text-white font-black rounded-xl px-4"
                >
                  Save
                </button>
              </div>
            </div>
          </div>

          {/* Her Turn box */}
          <div className="brutal-static bg-white rounded-2xl p-5">
            <div className="font-display font-black text-lg mb-1 flex items-center gap-2"><Gift size={18} /> The Her Turn Box</div>
            <p className="text-xs text-[#333] mb-4">Control the waitlist landing page. Pre-orders open automatically on the date below.</p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[#333]">Pre-orders open (YYYY-MM-DD)</label>
                <input
                  data-testid="herturn-opendate-input"
                  value={siteConfig.herturn_open_date || ""}
                  onChange={(e) => setSiteConfig({ ...siteConfig, herturn_open_date: e.target.value })}
                  placeholder="2026-10-30"
                  className="w-full mt-1 px-3 py-2 border-2 border-black rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[#333]">Ship date (display text)</label>
                <input
                  data-testid="herturn-shipdate-input"
                  value={siteConfig.herturn_ship_date || ""}
                  onChange={(e) => setSiteConfig({ ...siteConfig, herturn_ship_date: e.target.value })}
                  placeholder="December 6"
                  className="w-full mt-1 px-3 py-2 border-2 border-black rounded-xl"
                />
              </div>
            </div>

            <label className="flex items-center gap-3 font-bold text-sm mt-4">
              <input
                data-testid="herturn-reveal-toggle"
                type="checkbox"
                checked={!!siteConfig.herturn_reveal}
                onChange={(e) => setSiteConfig({ ...siteConfig, herturn_reveal: e.target.checked })}
              />
              Reveal box contents on the landing page
            </label>

            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-widest text-[#333]">Box contents (one item per line)</label>
              <textarea
                data-testid="herturn-items-input"
                rows={5}
                value={(siteConfig.herturn_items || []).join("\n")}
                onChange={(e) => setSiteConfig({ ...siteConfig, herturn_items: e.target.value.split("\n") })}
                placeholder={"Signature tee\nCandle\nSticker pack"}
                className="w-full mt-1 px-3 py-2 border-2 border-black rounded-xl font-mono text-sm"
              />
            </div>

            <button
              data-testid="herturn-save-btn"
              onClick={() => saveConfig({
                herturn_open_date: siteConfig.herturn_open_date || "",
                herturn_ship_date: siteConfig.herturn_ship_date || "",
                herturn_reveal: !!siteConfig.herturn_reveal,
                herturn_items: (siteConfig.herturn_items || []).map((s) => s.trim()).filter(Boolean),
              }, "Her Turn settings saved")}
              className="mt-4 inline-flex items-center gap-2 bg-[#EC008C] text-white font-black rounded-full px-5 py-2 brutal"
            >
              Save Her Turn settings
            </button>

            <div className="mt-5 border-t-2 border-black/10 pt-4">
              <div className="font-bold text-sm mb-2">Waitlist ({waitlist.length})</div>
              {waitlist.length === 0 ? (
                <div className="text-xs text-[#777]">No signups yet.</div>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-1" data-testid="herturn-waitlist-list">
                  {waitlist.map((w, i) => (
                    <div key={i} className="text-xs flex justify-between border-b border-black/5 py-1">
                      <span className="font-semibold">{w.email}</span>
                      <span className="text-[#777]">{w.name || "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && editing && (
        <ProductForm
          product={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </main>
  );
};

const ProductForm = ({ product, onClose, onSaved }) => {
  const [form, setForm] = useState({
    slug: product.slug || "",
    name: product.name || "",
    category: product.category || "backpacks",
    price: product.price || 0,
    description: product.description || "",
    images: product.images || [],
    colors: (product.colors || []).join(","),
    color_images: product.color_images || {},
    sizes: (product.sizes || []).join(","),
    processing_time: product.processing_time || "",
    featured: product.featured || false,
    stock: product.stock || 100,
    sort_order: product.sort_order ?? 100,
    tags: (product.tags || []).join(","),
    video: product.video || "",
    local_pickup: product.local_pickup || false,
    weight: product.weight ?? "",
    dimensions: product.dimensions || "",
    status: product.status || "draft",
    preorder: product.preorder || false,
    preorder_note: product.preorder_note || "",
    preorder_date: product.preorder_date || "",
    preorder_fulfillment: product.preorder_fulfillment || "ship",
    monogram_enabled: product.monogram_enabled || false,
    monogram_price: product.monogram_price ?? "",
    discount_exempt: product.discount_exempt || false,
    handling_enabled: product.handling_enabled || false,
    handling_first: product.handling_first ?? "",
    handling_additional: product.handling_additional ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((f) => ({ ...f, images: [...f.images, r.data.url] }));
      }
      toast.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const moveImage = (i, dir) => {
    setForm((f) => {
      const arr = [...f.images];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, images: arr };
    });
  };

  const removeImage = (i) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  };

  const addImageUrl = () => {
    const url = window.prompt("Paste image URL:");
    if (url && url.trim()) setForm((f) => ({ ...f, images: [...f.images, url.trim()] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      slug: form.slug,
      name: form.name,
      category: form.category,
      price: parseFloat(form.price),
      description: form.description,
      images: form.images,
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      color_images: Object.fromEntries(
        Object.entries(form.color_images || {}).filter(([, v]) => v && v.trim())
      ),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      processing_time: (form.processing_time || "").trim() || null,
      featured: form.featured,
      stock: parseInt(form.stock) || 100,
      sort_order: parseInt(form.sort_order) || 100,
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      video: form.video || null,
      local_pickup: form.local_pickup,
      weight: form.weight === "" || form.weight === null ? null : parseFloat(form.weight),
      dimensions: (form.dimensions || "").trim() || null,
      status: form.status || "draft",
      preorder: form.preorder,
      preorder_note: (form.preorder_note || "").trim() || null,
      preorder_date: (form.preorder_date || "").trim() || null,
      preorder_fulfillment: form.preorder_fulfillment || "ship",
      monogram_enabled: form.monogram_enabled,
      monogram_price: form.monogram_price === "" || form.monogram_price === null ? 0 : parseFloat(form.monogram_price),
      discount_exempt: form.discount_exempt,
      handling_enabled: form.handling_enabled,
      handling_first: form.handling_first === "" || form.handling_first === null ? 10 : parseFloat(form.handling_first),
      handling_additional: form.handling_additional === "" || form.handling_additional === null ? 5.99 : parseFloat(form.handling_additional),
    };
    try {
      if (product.id) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="brutal-lg bg-white rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black text-2xl">{product.id ? "Edit" : "New"} Product</h3>
          <button type="button" onClick={onClose}><X /></button>
        </div>
        <div className="space-y-3">
          <input required placeholder="Slug (url)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-slug" />
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-name" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-category">
              <option value="backpacks">Backpacks</option>
              <option value="tshirts">T-Shirts</option>
              <option value="travel">Travel</option>
              <option value="hbcu">HBCU</option>
              <option value="embroidery">Embroidery</option>
              <option value="kids">Kids & Books</option>
              <option value="bags">Bags</option>
              <option value="steam-kits">STEAM Kits</option>
              <option value="giftboxes">Gift Boxes (no discounts)</option>
              <option value="subscriptions">Subscriptions (no discounts)</option>
            </select>
            <input required type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-price" />
          </div>
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-description" />

          {/* Image manager */}
          <div className="border-2 border-black rounded-xl p-4 bg-[#FAFAF4]">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <ImageIcon size={16} className="text-[#EC008C]" />
                <span className="text-[10px] tracking-[0.28em] uppercase font-black text-black">Photos ({form.images.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="form-image-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="form-image-upload"
                  className="inline-flex items-center gap-1.5 bg-[#EC008C] text-white text-xs font-bold px-3 py-2 rounded-full border-2 border-[#EC008C] hover:bg-black hover:border-black disabled:opacity-60"
                >
                  <Upload size={12} /> {uploading ? "Uploading..." : "Upload photo"}
                </button>
                <button
                  type="button"
                  onClick={addImageUrl}
                  data-testid="form-image-add-url"
                  className="inline-flex items-center gap-1 text-xs font-bold text-black underline hover:text-[#EC008C]"
                >
                  + Paste URL
                </button>
              </div>
            </div>
            {form.images.length === 0 ? (
              <div className="text-xs text-[#666] italic">No photos yet. Upload from your computer or paste a URL.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {form.images.map((src, i) => (
                  <div key={`${src}-${i}`} className="relative border-2 border-black rounded-lg overflow-hidden bg-white aspect-square group" data-testid={`form-image-thumb-${i}`}>
                    <img src={src} alt="" className="w-full h-full object-contain" />
                    <div className="absolute top-1 left-1 bg-black text-white text-[10px] font-black px-1.5 py-0.5 rounded">
                      {i === 0 ? "MAIN" : i + 1}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-black/85 text-white flex items-center justify-between px-1 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} data-testid={`form-image-up-${i}`} className="p-1 hover:text-[#DCE842] disabled:opacity-30">
                        <ChevronUp size={14} />
                      </button>
                      <button type="button" onClick={() => moveImage(i, 1)} disabled={i === form.images.length - 1} data-testid={`form-image-down-${i}`} className="p-1 hover:text-[#DCE842] disabled:opacity-30">
                        <ChevronDown size={14} />
                      </button>
                      <button type="button" onClick={() => removeImage(i)} data-testid={`form-image-remove-${i}`} className="p-1 hover:text-[#EC008C]">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 text-[10px] text-[#555]">Hover a photo to reorder or delete. First image is the main photo.</div>
          </div>

          <input placeholder="Video URL (optional)" value={form.video} onChange={(e) => setForm({ ...form, video: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-video" />
          <input placeholder="Colors (comma-separated)" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-colors" />

          {/* Color → Image mapping: pick which uploaded photo shows when a color is selected */}
          {form.colors.split(",").map((c) => c.trim()).filter(Boolean).length > 0 && form.images.length > 0 && (
            <div className="border-2 border-[#000000] rounded-xl p-3 space-y-2 bg-[#FAFAF4]" data-testid="form-color-images">
              <div className="text-[11px] uppercase tracking-widest font-black">Color → Photo (optional)</div>
              <div className="text-[10px] text-[#555]">Bind a photo to each color. When a customer picks that color on the product page, the photo swaps automatically.</div>
              {form.colors.split(",").map((c) => c.trim()).filter(Boolean).map((c) => (
                <div key={c} className="flex items-center gap-3">
                  <div className="w-24 text-xs font-semibold uppercase tracking-wider">{c}</div>
                  <select
                    data-testid={`form-color-image-${c}`}
                    value={form.color_images?.[c] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, color_images: { ...(f.color_images || {}), [c]: e.target.value } }))}
                    className="flex-1 px-3 py-2 border border-[#000000] rounded text-xs bg-white"
                  >
                    <option value="">— No photo binding —</option>
                    {form.images.map((src, i) => (
                      <option key={i} value={src}>#{i + 1} · {src.split("/").pop()}</option>
                    ))}
                  </select>
                  {form.color_images?.[c] && (
                    <img src={form.color_images[c]} alt="" className="w-10 h-10 object-cover rounded border border-black" />
                  )}
                </div>
              ))}
            </div>
          )}

          <input placeholder="Sizes (comma-separated)" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-sizes" />

          <div>
            <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">
              Processing / Production Time
            </label>
            <input
              type="text"
              placeholder='e.g. "Ships in 3-5 business days" or "Made to order · 7-10 days"'
              value={form.processing_time}
              onChange={(e) => setForm({ ...form, processing_time: e.target.value })}
              className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl"
              data-testid="form-processing-time"
            />
            <div className="text-[10px] text-[#555] mt-1">
              Buyers must agree before checkout when this is set. Saved on the order for disputes.
            </div>
          </div>
          <input placeholder="Tags (comma-separated, cross-list in other filters e.g. hbcu)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-tags" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">Display Order</label>
              <input type="number" placeholder="100" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-sort-order" />
            </div>
            <div>
              <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">Stock</label>
              <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-stock" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm font-bold">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" /> Featured
          </label>

          <div className="border-2 border-black rounded-xl p-4 bg-white space-y-2">
            <label className="text-[10px] tracking-widest uppercase font-black text-black block">Visibility</label>
            <div className="flex gap-2">
              <button type="button" data-testid="form-visibility-draft" onClick={() => setForm({ ...form, status: "draft" })} className={`flex-1 py-2 rounded-full border-2 border-black text-xs font-black ${form.status === "draft" ? "bg-[#9CA3AF] text-black" : "bg-white text-black"}`}>Draft (hidden)</button>
              <button type="button" data-testid="form-visibility-live" onClick={() => setForm({ ...form, status: "live" })} className={`flex-1 py-2 rounded-full border-2 border-black text-xs font-black ${form.status === "live" ? "bg-[#2A9D8F] text-white" : "bg-white text-black"}`}>Live</button>
            </div>
            <div className="text-[10px] text-[#555]">Draft products never appear on the storefront.</div>
          </div>

          <div className="border-2 border-black rounded-xl p-4 bg-[#FFF0F8] space-y-3">
            <label className="flex items-center gap-2 text-sm font-black">
              <input type="checkbox" checked={form.preorder} onChange={(e) => setForm({ ...form, preorder: e.target.checked })} data-testid="form-preorder" />
              Pre-order item
            </label>
            {form.preorder && (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">Pre-order note (shown to customers)</label>
                  <input type="text" placeholder="e.g. Ships in 4-6 weeks" value={form.preorder_note} onChange={(e) => setForm({ ...form, preorder_note: e.target.value })} className="w-full px-4 py-3 border-2 border-black rounded-xl" data-testid="form-preorder-note" />
                </div>
                <div>
                  <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">Estimated ship / pickup date</label>
                  <input type="text" placeholder="e.g. Available after October 15" value={form.preorder_date} onChange={(e) => setForm({ ...form, preorder_date: e.target.value })} className="w-full px-4 py-3 border-2 border-black rounded-xl" data-testid="form-preorder-date" />
                </div>
                <div>
                  <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">When ready, fulfill by</label>
                  <select value={form.preorder_fulfillment} onChange={(e) => setForm({ ...form, preorder_fulfillment: e.target.value })} className="w-full px-4 py-3 border-2 border-black rounded-xl bg-white" data-testid="form-preorder-fulfillment">
                    <option value="ship">Ship only</option>
                    <option value="pickup">Local pickup only</option>
                    <option value="both">Let customer choose</option>
                  </select>
                </div>
                <div className="text-[10px] text-[#555]">Full payment is collected at checkout via Stripe.</div>
              </div>
            )}
          </div>

          <div className="border-2 border-[#000000] rounded-xl p-4 bg-[#FFFBEA] space-y-3">
            <label className="flex items-center gap-2 text-sm font-black">
              <input type="checkbox" checked={form.local_pickup} onChange={(e) => setForm({ ...form, local_pickup: e.target.checked })} data-testid="form-local-pickup" />
              Available for Local Pickup
            </label>
            <div className="text-[10px] text-[#555] -mt-1">Customers can choose to pick this item up locally (no shipping fee) at checkout.</div>
            <div className="border-t-2 border-black/10 pt-3">
              <label className="flex items-center gap-2 text-sm font-black">
                <input type="checkbox" checked={form.monogram_enabled} onChange={(e) => setForm({ ...form, monogram_enabled: e.target.checked })} data-testid="form-monogram-enabled" />
                Offer Monogram (initials)
              </label>
              <div className="text-[10px] text-[#555] mt-1 mb-2">Lets customers add up to 3 embroidered initials. Set price to 0 for a free add-on.</div>
              {form.monogram_enabled && (
                <div>
                  <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">Monogram price ($, 0 = free)</label>
                  <input type="number" step="0.01" placeholder="0.00" value={form.monogram_price} onChange={(e) => setForm({ ...form, monogram_price: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-monogram-price" />
                </div>
              )}
            </div>
            <div className="border-t-2 border-black/10 pt-3">
              <label className="flex items-center gap-2 text-sm font-black">
                <input type="checkbox" checked={form.discount_exempt} onChange={(e) => setForm({ ...form, discount_exempt: e.target.checked })} data-testid="form-discount-exempt" />
                No discount codes (gift box / subscription)
              </label>
              <div className="text-[10px] text-[#555] mt-1">Public codes (WELCOME10, promos) won't apply to this item. Gift boxes & subscriptions are blocked automatically; only per-user codes can discount them.</div>
            </div>
            <div className="border-t-2 border-black/10 pt-3">
              <label className="flex items-center gap-2 text-sm font-black">
                <input type="checkbox" checked={form.handling_enabled} onChange={(e) => setForm({ ...form, handling_enabled: e.target.checked })} data-testid="form-handling-enabled" />
                Add Handling fee (per box / unit)
              </label>
              <div className="text-[10px] text-[#555] mt-1 mb-2">Charges a handling add-in per unit. The first unit uses the "first" price; every additional unit adds the "each additional" price. Example: 5 boxes = first + 4 × additional.</div>
              {form.handling_enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">First unit ($)</label>
                    <input type="number" step="0.01" placeholder="10.00" value={form.handling_first} onChange={(e) => setForm({ ...form, handling_first: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-handling-first" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">Each additional ($)</label>
                    <input type="number" step="0.01" placeholder="5.99" value={form.handling_additional} onChange={(e) => setForm({ ...form, handling_additional: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-handling-additional" />
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">Weight (lb)</label>
                <input type="number" step="0.01" placeholder="e.g. 1.5" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-weight" />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase font-black text-black block mb-1">Dimensions</label>
                <input type="text" placeholder='e.g. 12x9x4 in' value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} className="w-full px-4 py-3 border-2 border-[#000000] rounded-xl" data-testid="form-dimensions" />
              </div>
            </div>
            <div className="text-[10px] text-[#555]">Weight &amp; dimensions are used for the PirateShip label export (optional).</div>
          </div>

          <button disabled={saving} data-testid="form-save" className="w-full bg-[#EC008C] text-[#FFFFFF] font-bold py-3 rounded-full brutal">
            {saving ? "Saving..." : "Save Product"}
          </button>
        </div>
      </form>
    </div>
  );
};

const MetaCell = ({ label, value }) => (
  <div className="border-2 border-black rounded-xl px-3 py-2 bg-[#FAFAF4]">
    <div className="text-[9px] uppercase tracking-widest font-black text-[#EC008C] mb-0.5">{label}</div>
    <div className="text-sm text-black font-semibold truncate">{value}</div>
  </div>
);

export default AdminDashboard;
